// src/components/SignUp.js
import React, { useState, useEffect } from 'react';
import { signUpWithEmailPassword, deleteUser, signInWithGoogle } from '../services/firebase';
import { api } from '../services/api';
import { useNavigate, Link } from 'react-router-dom';
import '../styles/forms.css';
import '../styles/HomePage.css';

const SignUp = ({ setUser }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteToken, setInviteToken] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setInviteToken(params.get('invite_token') || '');
  }, []);

  const getFirebaseErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'The email address is already in use. Please use a different email.';
      case 'auth/invalid-email':
        return 'The email address is invalid. Please enter a valid email.';
      case 'auth/weak-password':
        return 'The password is too weak. Please choose a stronger password.';
      case 'auth/user-not-found':
        return 'No user found with this email address. Please sign up first.';
      case 'auth/wrong-password':
        return 'The password is incorrect. Please try again.';
      case 'auth/network-request-failed':
        return 'A network error occurred. Please check your internet connection and try again.';
      default:
        return 'An unexpected error occurred. Please try again later.';
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    let credentials;

    try {
      credentials = await signUpWithEmailPassword(email, password);

      const idToken = await credentials.getIdToken();
      const response = await api.post('signup', {
        email: email,
        external_id: credentials.uid,
        id_token: idToken,
        invitation_token: inviteToken 
      });

      if (response.status !== 200) {
        throw new Error('Failed to sync user with backend');
      }

      const data = await response.data;
      const { token, settings, invitation_accepted, pending_family_invitations } = data;
      localStorage.setItem('token', token);
      localStorage.setItem('userSettings', JSON.stringify(settings));
      if (pending_family_invitations) {
        localStorage.setItem('pendingFamilyInvitations', JSON.stringify(pending_family_invitations));
      }
      if (invitation_accepted) {
        localStorage.setItem('invitationAccepted', 'true');
      }

      setUser(credentials);

      console.log('User signed up successfully');
      navigate('/');
    } catch (error) {
      console.error('Error during sign-up:', error);

      if (credentials && credentials.user) {
        console.log('Deleting Firebase user due to backend failure...');
        await deleteUser(credentials.user);
      }

      const errorCode = error.code;
      const errorMessage = getFirebaseErrorMessage(errorCode);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const startSignInWithGoogle = async () => {
    localStorage.setItem('redirectedFrom', 'signup');
    localStorage.setItem('inviteToken', inviteToken);
    await signInWithGoogle();
    // The redirect will handle the rest, including syncing with the backend
    navigate('/'); // Redirect to home page to initiate Google sign-in
  }

  return (
    <div className="container">
      <div className='homepage-card'>
        <h1>Sign Up</h1>
        {error && <p className='error'>{error}</p>}
        <form onSubmit={handleSignUp}>
          <div className="form-group">
            <label>Email
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
          </div>

          <div className="form-group">
            <label>Password
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
          </div>

          <div className="button-container">
            <button className='login-button' type="submit" disabled={loading}>
              {loading ? 'Signing Up...' : 'Sign Up'}
            </button>

            <p className="or-text">or</p>

            <button
              className="login-button"
              type="button"
              onClick={startSignInWithGoogle}
            >
              <svg className="button-icon" viewBox="0 0 48 48" width="24" height="24">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              Sign Up with Google
            </button>
          </div>

          <p className="forgot-password-link">
            Already have an account?{' '}
            <Link to={inviteToken ? `/login?invite_token=${encodeURIComponent(inviteToken)}` : '/login'} className="link-button">
              Log in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default SignUp;